import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FileCheck2, Filter, Plus, X } from "lucide-react";

import { useCreateSupplierQuote, useSupplierQuotes } from "@/api/quotes";
import { useSuppliers } from "@/api/queries";
import type { QuoteReceivedVia, SupplierQuote, SupplierQuoteStatus } from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Field, Input, Select } from "@/components/Input";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { formatDate, pluralize } from "@/lib/format";
import {
  extractionTone,
  formatExtractionState,
  formatQuoteStatus,
  quoteTone,
} from "@/lib/quote-display";

const PAGE_SIZE = 50;

const STATUSES: SupplierQuoteStatus[] = [
  "RECEIVED",
  "UNDER_REVIEW",
  "VALIDATED",
  "SUPERSEDED",
  "REJECTED",
];

const VIA: QuoteReceivedVia[] = ["MANUAL_UPLOAD", "EMAIL", "MANUAL_ENTRY"];

/**
 * Les soumissions fournisseur (V1.6-06).
 *
 * Une soumission **rapporte** ce qu'un fournisseur affirme. Elle ne choisit
 * personne et ne commande rien — et tant qu'un humain ne l'a pas validée, ses
 * chiffres ne sont que des valeurs lues.
 *
 * C'est pour ça que la liste ne montre un montant que sur une soumission
 * validée : afficher un total extrait à côté d'un total validé, dans la même
 * colonne, ferait comparer deux choses qui ne se comparent pas.
 */
export function SupplierQuotesPage() {
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);

  const status = params.get("status") ?? undefined;
  const offset = Number(params.get("offset") ?? 0);

  const query = useMemo(
    () => ({
      status: status as SupplierQuoteStatus | undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [status, offset],
  );

  const { data, isLoading, error, refetch } = useSupplierQuotes(query);

  function update(next: Record<string, string | undefined>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === "") merged.delete(key);
      else merged.set(key, value);
    }
    if (!("offset" in next)) merged.delete("offset");
    setParams(merged, { replace: true });
  }

  return (
    <Page>
      <PageHeader
        title="Soumissions"
        subtitle="Ce que les fournisseurs répondent. Une soumission n'est pas un achat."
        actions={
          <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden />
            Nouvelle soumission
          </Button>
        }
      />

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="flex shrink-0 flex-col gap-2 border-b border-border p-3 sm:flex-row sm:items-center">
          <Select
            value={status ?? ""}
            onChange={(event) => update({ status: event.target.value || undefined })}
            aria-label="Filtrer par statut"
            className="sm:w-60"
          >
            <option value="">Tous les statuts</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {formatQuoteStatus(value)}
              </option>
            ))}
          </Select>
          {status && (
            <button
              type="button"
              onClick={() => setParams(new URLSearchParams(), { replace: true })}
              className="flex h-10 items-center gap-1.5 rounded-md px-2 text-[13px] text-ink-subtle hover:text-ink"
            >
              <X className="size-4" aria-hidden />
              Effacer
            </button>
          )}
        </div>

        <div className="min-w-0">
          {isLoading && <TableSkeleton rows={8} columns={6} />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}

          {data && data.items.length === 0 && (
            <EmptyState
              icon={
                status ? (
                  <Filter className="size-6" aria-hidden />
                ) : (
                  <FileCheck2 className="size-6" aria-hidden />
                )
              }
              title={status ? "Aucune soumission ne correspond" : "Aucune soumission"}
              description={
                status
                  ? "Essayez un autre statut."
                  : "Les réponses chiffrées des fournisseurs apparaîtront ici."
              }
            />
          )}

          {data && data.items.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="w-32 whitespace-nowrap">Code</Th>
                      <Th className="max-w-0">Fournisseur</Th>
                      <Th className="hidden whitespace-nowrap @4xl:table-cell">Demande</Th>
                      <Th className="whitespace-nowrap">Reçue</Th>
                      <Th className="whitespace-nowrap">Validité</Th>
                      <Th align="right" className="whitespace-nowrap">
                        Montant
                      </Th>
                      <Th>Statut</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item) => (
                      <QuoteRow key={item.id} item={item} />
                    ))}
                  </tbody>
                </TableShell>
              </div>

              <ul className="divide-y divide-border/60 md:hidden">
                {data.items.map((item) => (
                  <li key={item.id}>
                    <QuoteCard item={item} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {data && data.total > PAGE_SIZE && (
          <Pagination
            offset={offset}
            limit={PAGE_SIZE}
            total={data.total}
            onChange={(next) => update({ offset: String(next) })}
          />
        )}
      </div>

      <NewQuoteDialog open={creating} onOpenChange={setCreating} />
    </Page>
  );
}

/**
 * Le montant, et seulement quand il veut dire quelque chose.
 *
 * Sur une soumission validée, c'est un chiffre qu'un humain a accepté. Avant,
 * c'est une valeur lue — et l'écrire sans le dire ferait croire à un total
 * vérifié.
 */
function amountLabel(item: SupplierQuote): string {
  if (item.status !== "VALIDATED") return "à vérifier";
  if (!item.supplier_total) return "—";
  return `${item.supplier_total} ${item.currency ?? ""}`.trim();
}

function QuoteRow({ item }: { item: SupplierQuote }) {
  return (
    <Tr>
      <Td className="whitespace-nowrap font-mono text-[12px] text-ink-muted">
        <Link to={`/quotes/${item.id}`} className="hover:text-brand">
          {item.code}
        </Link>
      </Td>
      <Td className="max-w-0">
        <Link to={`/quotes/${item.id}`} className="block min-w-0 hover:text-brand">
          <span className="block truncate font-medium text-ink">{item.supplier_name}</span>
          <span className="block truncate text-[11px] text-ink-subtle">
            {item.quote_number ?? "sans numéro"}
            {item.revision > 1 ? ` · révision ${item.revision}` : ""}
          </span>
        </Link>
      </Td>
      <Td className="hidden whitespace-nowrap font-mono text-[12px] text-ink-muted @4xl:table-cell">
        {item.rfq_code ?? "—"}
      </Td>
      <Td className="whitespace-nowrap text-[12px] text-ink-muted">
        {formatDate(item.received_at)}
      </Td>
      <Td className="whitespace-nowrap text-[12px] text-ink-muted">
        {item.valid_until ? formatDate(item.valid_until) : "—"}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink-muted">
        {amountLabel(item)}
      </Td>
      <Td>
        <StatusBadge tone={quoteTone(item.status)}>
          {formatQuoteStatus(item.status)}
        </StatusBadge>
        {item.status !== "VALIDATED" && item.extraction_state !== "NOT_STARTED" && (
          <StatusBadge tone={extractionTone(item.extraction_state)} className="ml-1">
            {formatExtractionState(item.extraction_state)}
          </StatusBadge>
        )}
      </Td>
    </Tr>
  );
}

function QuoteCard({ item }: { item: SupplierQuote }) {
  return (
    <Link
      to={`/quotes/${item.id}`}
      className="flex min-h-[64px] items-center gap-3 px-3 py-3 active:bg-surface-hover"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-ink">{item.supplier_name}</p>
        <p className="truncate font-mono text-[11px] text-ink-subtle">{item.code}</p>
        <p className="mt-0.5 truncate text-[12px] text-ink-muted">
          {formatDate(item.received_at)} · {pluralize(item.line_count, "ligne")} ·{" "}
          {amountLabel(item)}
        </p>
      </div>
      <StatusBadge tone={quoteTone(item.status)} className="shrink-0">
        {formatQuoteStatus(item.status)}
      </StatusBadge>
    </Link>
  );
}

function NewQuoteDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const createQuote = useCreateSupplierQuote();
  const { data: suppliers } = useSuppliers({ limit: 200 }, { enabled: open });

  const [supplierId, setSupplierId] = useState("");
  const [via, setVia] = useState<QuoteReceivedVia>("MANUAL_ENTRY");
  const [quoteNumber, setQuoteNumber] = useState("");
  const [quoteDate, setQuoteDate] = useState("");
  const [currency, setCurrency] = useState("");

  function reset() {
    setSupplierId("");
    setVia("MANUAL_ENTRY");
    setQuoteNumber("");
    setQuoteDate("");
    setCurrency("");
  }

  async function submit() {
    if (!supplierId) return;
    try {
      const created = await createQuote.mutateAsync({
        supplier_id: supplierId,
        received_via: via,
        quote_number: quoteNumber,
        quote_date: quoteDate,
        currency: currency.trim().toUpperCase(),
      });
      toast.success("Soumission enregistrée", `${created.quote.code} — à vérifier`);
      reset();
      onOpenChange(false);
    } catch (failure) {
      toast.error("Soumission non enregistrée", String(failure));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Nouvelle soumission"
      description="Ce que le fournisseur a répondu. Rien n'est utilisable avant vérification."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={!supplierId || createQuote.isPending}
          >
            {createQuote.isPending ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Fournisseur">
          <Select
            value={supplierId}
            onChange={(event) => setSupplierId(event.target.value)}
            aria-label="Fournisseur"
          >
            <option value="">Choisir…</option>
            {suppliers?.items.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Comment elle est arrivée">
          <Select
            value={via}
            onChange={(event) => setVia(event.target.value as QuoteReceivedVia)}
            aria-label="Comment elle est arrivée"
          >
            {VIA.map((value) => (
              <option key={value} value={value}>
                {value === "MANUAL_ENTRY"
                  ? "Saisie (téléphone, comptoir)"
                  : value === "EMAIL"
                    ? "Courriel"
                    : "Document déposé"}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Numéro du fournisseur" hint="Facultatif : beaucoup n'en mettent pas.">
          <Input
            value={quoteNumber}
            onChange={(event) => setQuoteNumber(event.target.value)}
            aria-label="Numéro du fournisseur"
          />
        </Field>

        <div className="flex gap-3">
          <div className="flex-1">
            <Field label="Date de la soumission" hint="Facultative.">
              <Input
                type="date"
                value={quoteDate}
                onChange={(event) => setQuoteDate(event.target.value)}
                aria-label="Date de la soumission"
              />
            </Field>
          </div>
          <div className="w-32">
            <Field label="Devise" hint="Jamais devinée.">
              <Input
                value={currency}
                onChange={(event) => setCurrency(event.target.value)}
                placeholder="CAD"
                maxLength={3}
                aria-label="Devise"
              />
            </Field>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
